import React from "react";
import type { Editor } from "@tiptap/react";
import {
  Comment,
  comment as c,
  Identifiable,
  project as p,
  ProjectResource,
} from "@planzone/dto";
import { useProjectResourcesRetrieval } from "@planzone/view-models";
import { RichTextInput } from "@common/richTextInput";
import { ResourceAllocationMenu } from "@common/resourceAllocationMenu";
import { AddMemberIcon } from "@scenes/comments/form/addMemberIcon";
import { Button } from "@common/button";
import { useTranslation } from "react-i18next";
import { useMessage } from "@components/useMessage";
import { events } from "@planzone/events";
import { Props } from "./types";
import styles from "./styles.scss";

function resolveRecipients(
  resources: Identifiable<ProjectResource>[],
  html: string,
  excludeNames: string[] = [],
): Identifiable<ProjectResource>[] {
  return resources.filter(
    ({ resource }) =>
      !excludeNames.includes(resource.name) &&
      html.includes(`data-id="${resource.uuid}"`),
  );
}

function isEmptyHtml(html: string): boolean {
  return !html || html.replace(/<[^>]*>/g, "").trim().length === 0;
}

function ChatInput({
  isSubmitting,
  project,
  replyTo,
  onCancelReply,
  submit: submitForm,
}: Props) {
  const { t } = useTranslation();
  const editorRef = React.useRef<Editor | null>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [content, setContent] = React.useState("");
  const [isMenuOpened, setIsMenuOpened] = React.useState(false);
  const [comment, setComment] = React.useState(c);
  const [pendingComment, setPendingComment] = React.useState<Comment | null>(
    null,
  );
  const canSubmit = !isEmptyHtml(content);

  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    projectResources,
    retrieveProjectResources,
  } = useProjectResourcesRetrieval({ ...p, uuid: project });

  React.useEffect(() => {
    !!project &&
      !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveProjectResources({ ...p, uuid: project });
  }, [project]);

  React.useEffect(() => {
    if (!isMenuOpened) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpened(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpened]);

  React.useEffect(() => {
    if (replyTo && editorRef.current) {
      editorRef.current
        .chain()
        .focus()
        .setContent(`<blockquote>${replyTo.content}</blockquote><p></p>`)
        .run();
      editorRef.current
        .chain()
        .focus("end")
        .insertContent({
          type: "mention",
          attrs: { id: replyTo.author.uuid, label: replyTo.author.name },
        })
        .insertContent(" ")
        .run();
    }
  }, [replyTo?.uuid]);

  const mentions = React.useMemo(
    () =>
      projectResources.map(({ resource }) => ({
        id: resource.uuid,
        label: resource.name,
        resource,
      })),
    [projectResources],
  );

  const submitComment = (
    comment: Comment,
    projectResources: Identifiable<ProjectResource>[],
  ) => {
    const recipients = resolveRecipients(
      projectResources,
      content,
      replyTo ? [replyTo.author.name] : [],
    );
    submitForm({
      ...comment,
      content,
      recipients: recipients.map(({ resource }) => resource),
      ...(replyTo && { replyTo }),
    });
  };

  const submit = (
    comment: Comment,
    projectResources: Identifiable<ProjectResource>[],
  ) => {
    if (isRequestSuccess) {
      submitComment(comment, projectResources);
    } else {
      retrieveProjectResources({ ...p, uuid: project });
      setPendingComment(comment);
    }
  };

  useMessage((message) => {
    if (message.type === events.postCommentSucceeded) {
      setContent("");
      setComment({ ...comment, content: "", recipients: [] });
      editorRef.current?.commands.clearContent();
    }

    if (
      message.type === events.retrieveProjectResourcesSucceeded &&
      pendingComment
    ) {
      submitComment(comment, message.data.projectResources);
      setPendingComment(null);
    }
  });

  return (
    <div className={styles.chatInput}>
      <RichTextInput
        mode="comment"
        className={styles.noBorder}
        value=""
        placeholder={t("comment.placeholder")}
        isDisabled={isSubmitting}
        editorRef={editorRef}
        maxLength={10000}
        mentions={mentions}
        input={(html) => setContent(html)}
        onEnter={() => {
          if (canSubmit && !isSubmitting) {
            submit(comment, projectResources);
            return true;
          }
          return false;
        }}
      />
      <div className={styles.footer}>
        <div className={styles.iconBox} ref={menuRef}>
          <div
            className={styles.iconBox}
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpened(!isMenuOpened);
            }}
          >
            <AddMemberIcon className={styles.icon} />
          </div>
          {isMenuOpened && (
            <div className={styles.list}>
              <ResourceAllocationMenu
                allocatedResources={[]}
                openedViews={["resources"]}
                select={(resources) => {
                  const nodes = resources.flatMap((resource, index) => [
                    ...(index > 0
                      ? [{ type: "text" as const, text: " " }]
                      : []),
                    {
                      type: "mention" as const,
                      attrs: { id: resource.uuid, label: resource.name },
                    },
                  ]);
                  editorRef.current
                    ?.chain()
                    .focus()
                    .insertContent([...nodes, { type: "text", text: " " }])
                    .run();
                  setIsMenuOpened(false);
                }}
                unSelect={() => {}}
              />
            </div>
          )}
        </div>
        <Button
          type="submit"
          className={styles.submitButton}
          status={isSubmitting ? "pending" : "none"}
          isDisabled={!canSubmit}
          press={() => submit(comment, projectResources)}
        >
          {t("comment.submit")}
        </Button>
      </div>
    </div>
  );
}

export { ChatInput };
